const APP_AGENT_FRAMEWORK_CONTENT = `# App Agent Development Framework

## Overview
A comprehensive markdown file and boilerplate repository for initializing AI agents to build new applications from scratch.

## Purpose
This framework serves as:
- **AI System Prompt**: Initialize AI agents with proper context
- **Boilerplate Repository**: Starting point for new app development
- **Best Practices Guide**: Architectural patterns and conventions
- **Development Checklist**: Ensure nothing is missed

## Framework Structure

### 1. Project Initialization Prompt

You are an expert mobile/web application developer. You will build a new app using the following specifications:

## Project Overview
- App Name: {APP_NAME}
- Platform: {PLATFORM}
- Tech Stack: {TECH_STACK}
- Target Audience: {TARGET_AUDIENCE}

## Core Features
{FEATURE_LIST}

## Technical Requirements
- Architecture: {ARCHITECTURE_PATTERN}
- State Management: {STATE_MANAGEMENT}
- Backend: {BACKEND_SETUP}
- Database: {DATABASE}
- Authentication: {AUTH_METHOD}

## Development Standards
- Follow {STYLE_GUIDE} coding standards
- Write unit tests for all business logic
- Use TypeScript/strict typing
- Implement proper error handling
- Follow SOLID principles
- Document all public APIs

## Deliverables
1. Functional application code
2. Unit tests (80%+ coverage)
3. README with setup instructions
4. API documentation
5. Deployment configuration

### 2. Boilerplate Repository Structure

\`\`\`
app-boilerplate/
├── README.md
├── .gitignore
├── package.json
├── tsconfig.json
├── .env.example
├── docs/
│   ├── ARCHITECTURE.md
│   ├── API.md
│   ├── DEPLOYMENT.md
│   └── CONTRIBUTING.md
├── src/
│   ├── index.ts
│   ├── config/
│   │   ├── index.ts
│   │   └── constants.ts
│   ├── core/
│   │   ├── models/
│   │   ├── services/
│   │   └── utils/
│   ├── features/
│   │   └── example/
│   │       ├── components/
│   │       ├── hooks/
│   │       ├── services/
│   │       └── types/
│   ├── shared/
│   │   ├── components/
│   │   ├── hooks/
│   │   ├── types/
│   │   └── utils/
│   └── api/
│       ├── routes/
│       ├── controllers/
│       ├── middleware/
│       └── validators/
├── tests/
│   ├── unit/
│   ├── integration/
│   └── e2e/
└── scripts/
    ├── setup.sh
    ├── deploy.sh
    └── test.sh
\`\`\`

### 3. Technology Stack Templates

#### React Native + Firebase
- Frontend: React Native
- State Management: Redux Toolkit / Zustand
- Backend: Firebase Functions
- Database: Firestore
- Authentication: Firebase Auth
- Storage: Firebase Storage
- Notifications: FCM

Packages: react-native, @react-navigation/native, react-native-firebase, @reduxjs/toolkit, react-hook-form, axios

#### Next.js + Supabase
- Frontend: Next.js 14
- State Management: React Context / Zustand
- Backend: Next.js API Routes
- Database: PostgreSQL (Supabase)
- Authentication: Supabase Auth
- Storage: Supabase Storage

Packages: next, react, @supabase/supabase-js, tailwindcss, shadcn-ui, zod

#### Flutter + Node.js
- Frontend: Flutter
- State Management: Riverpod / Bloc
- Backend: Node.js + Express
- Database: MongoDB / PostgreSQL
- Authentication: JWT
- API: REST / GraphQL

### 4. Architectural Patterns

#### Clean Architecture
Domain Layer (Business Logic)
- Entities
- Use Cases
- Repository Interfaces

Data Layer (External)
- Repository Implementations
- Data Sources (API, Local DB)
- Models (DTOs)

Presentation Layer (UI)
- Views/Screens
- ViewModels/Controllers
- Widgets/Components

#### Feature-First Structure
features/
- authentication/
  - presentation/
  - domain/
  - data/
- user-profile/
  - presentation/
  - domain/
  - data/
- settings/
  - presentation/
  - domain/
  - data/

### 5. Code Generation Templates

#### Model Template
\`\`\`typescript
// src/core/models/base.model.ts
export interface BaseModel {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

// src/features/{feature}/models/{entity}.model.ts
export interface {Entity} extends BaseModel {
  // Entity-specific fields
}
\`\`\`

#### Service Template
\`\`\`typescript
// src/features/{feature}/services/{entity}.service.ts
export class {Entity}Service {
  constructor(private repository: I{Entity}Repository) {}
  
  async getById(id: string): Promise<{Entity}> {
    // Implementation
  }
  
  async create(data: Create{Entity}DTO): Promise<{Entity}> {
    // Implementation
  }
  
  async update(id: string, data: Update{Entity}DTO): Promise<{Entity}> {
    // Implementation
  }
  
  async delete(id: string): Promise<void> {
    // Implementation
  }
}
\`\`\`

#### API Route Template
\`\`\`typescript
// src/api/routes/{entity}.routes.ts
import { Router } from 'express';
import { {Entity}Controller } from '../controllers/{entity}.controller';
import { authenticate } from '../middleware/auth';
import { validate } from '../middleware/validation';

const router = Router();
const controller = new {Entity}Controller();

router.get('/', authenticate, controller.getAll);
router.get('/:id', authenticate, controller.getById);
router.post('/', authenticate, validate(Create{Entity}Schema), controller.create);
router.put('/:id', authenticate, validate(Update{Entity}Schema), controller.update);
router.delete('/:id', authenticate, controller.delete);

export default router;
\`\`\`

### 6. Development Workflow

## Development Process

1. **Planning Phase**
   - Define requirements
   - Create user stories
   - Design database schema
   - Design API contracts
   - Create mockups/wireframes

2. **Setup Phase**
   - Clone boilerplate
   - Configure environment
   - Set up CI/CD
   - Initialize version control
   - Configure linting/formatting

3. **Development Phase**
   - Implement data models
   - Create repository layer
   - Build business logic
   - Implement API endpoints
   - Create UI components
   - Write unit tests
   - Integration testing

4. **Testing Phase**
   - Unit tests (80%+ coverage)
   - Integration tests
   - E2E tests
   - Performance testing
   - Security audit

5. **Deployment Phase**
   - Configure production environment
   - Set up monitoring
   - Deploy backend
   - Deploy frontend
   - Smoke tests in production

6. **Post-Launch**
   - Monitor errors
   - Gather user feedback
   - Plan iterations
   - Performance optimization

### 7. Best Practices Checklist

## Code Quality
- Follow consistent naming conventions
- Use TypeScript strict mode
- Implement proper error handling
- Add comprehensive logging
- Write self-documenting code
- Use dependency injection
- Avoid code duplication (DRY)
- Keep functions small and focused

## Security
- Validate all inputs
- Sanitize user data
- Use environment variables for secrets
- Implement rate limiting
- Add authentication/authorization
- Use HTTPS only
- Implement CSRF protection
- Regular dependency updates

## Performance
- Optimize database queries
- Implement caching strategies
- Lazy load components
- Optimize images/assets
- Bundle size optimization
- Use CDN for static assets
- Implement pagination
- Profile and optimize bottlenecks

## Testing
- Unit tests for business logic
- Integration tests for APIs
- E2E tests for critical paths
- Test error scenarios
- Mock external dependencies
- Continuous integration
- Test coverage reports

## Documentation
- README with setup instructions
- API documentation
- Architecture documentation
- Code comments for complex logic
- Changelog maintenance
- Contributing guidelines

### 8. AI Agent Instructions

## How to Use This Framework

When starting a new app development:

1. **Initialize Context**
   - Read this entire framework document
   - Understand the architecture patterns
   - Review the tech stack options

2. **Gather Requirements**
   - Ask clarifying questions about the app
   - Confirm technical choices
   - Understand constraints

3. **Generate Boilerplate**
   - Create project structure
   - Set up configuration files
   - Initialize version control

4. **Implement Features**
   - Follow feature-first approach
   - Use template patterns
   - Write tests alongside code
   - Document as you go

5. **Quality Assurance**
   - Run linters and formatters
   - Execute test suite
   - Perform code review
   - Check security vulnerabilities

6. **Deployment Preparation**
   - Create deployment configuration
   - Set up environment variables
   - Write deployment documentation
   - Create monitoring alerts

## Code Generation Patterns

### For Each New Feature:
1. Create feature directory
2. Define models/types
3. Implement repository layer
4. Create service/business logic
5. Build API endpoints
6. Create UI components
7. Write tests
8. Update documentation

### For Each API Endpoint:
1. Define request/response types
2. Create validation schema
3. Implement controller method
4. Add middleware (auth, validation)
5. Write route handler
6. Add API documentation
7. Write integration tests

### For Each UI Component:
1. Define component props interface
2. Create component file
3. Add styling
4. Implement state management
5. Add event handlers
6. Write component tests
7. Create Storybook story (if applicable)

### 9. Quick Start Commands

\`\`\`bash
# Setup
npm install
cp .env.example .env
npm run setup

# Development
npm run dev
npm run test:watch
npm run lint

# Testing
npm run test
npm run test:coverage
npm run test:e2e

# Build
npm run build
npm run type-check

# Deploy
npm run deploy:staging
npm run deploy:prod
\`\`\`

### 10. Integration with App Success Platform

## Platform Integration

This boilerplate integrates with the App Success Platform:

- **App Registration**: Register your app to get appId
- **Translation**: Automatic localization integration
- **Analytics**: Built-in tracking
- **Notifications**: FCM integration ready
- **Legal**: Generated ToS/Privacy Policy
- **Icon**: Use generated app icons

Configuration:
1. Set APP_ID in environment variables
2. Configure Firebase project
3. Enable required platform features
4. Deploy with platform integration

## Maintenance & Updates

This framework document should be:
- Version controlled in Git
- Updated with new patterns
- Reviewed quarterly
- Enhanced based on learnings
- Shared across team

## Usage in Platform

Store in Firestore and allow users to:
- View and edit framework
- Fork for custom variations
- Export as markdown
- Initialize new projects from framework
`;

export default APP_AGENT_FRAMEWORK_CONTENT;
